import React from 'react'

import { FlaskConical } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

import { useColorMode, useIsDemoCompany } from 'hooks'
import { EXAMPLE_KIND_LABEL, EXAMPLE_KIND_TOOLTIP, type ExampleKind } from '@repo/types'
// Sibling path, not the `components` barrel: this file is exported FROM that barrel,
// so going through it would be a cycle. Same reason `AnnouncementKindBadge` and
// `SupportTypeBadge` state — and the same reason `InfoTooltip` below is reached by path.
import { STATUS_TOKEN_H, statusTone } from '../StatusChip/StatusChip'
import { InfoTooltip } from '../../feedback/InfoTooltip'

/**
 * ONE badge for "this row is not real customer data", rendered identically on every
 * surface a seeded row can appear (VRG-361, §36).
 *
 * ## What it is saying, and why it has to appear everywhere
 *
 * An acquisition demo is provisioned FROM a prospect's public website — scraped logo,
 * guessed product copy, a live certificate — INTO the same company row the prospect
 * later becomes. The seeded rows live on beside their real data forever, and nothing
 * else in the UI distinguishes them. A brand looking at their own catalogue would see
 * Vera's guesses about their products presented exactly like the ones they entered
 * themselves.
 *
 * `intro` is the same mechanism for sample/onboarding data. Different provenance, same
 * two things the reader needs to know, so one badge covers both.
 *
 * ## CORRECTION (VRG-836): TWO things convert, and this docblock denied both
 *
 * It used to assert "there is no conversion event: the company was always a company",
 * and, below, that "a demo product does not become a real one". Both are false, and the
 * first was load-bearing for the wrong conclusion.
 *
 * **The company converts.** `TakeOverDemoUseCase` (VRG-361 slice 2,
 * `apps/api/.../demo/take-over-demo/take-over-demo.use-case.ts`) converts a demoed
 * prospect on the SAME company row in one transaction: it stamps `demo_taken_over_at`
 * and moves `kind` off `'demo'` to `'standard'` in the same statement, because
 * `companies_demo_takeover_guard` requires both at once. It deliberately leaves the
 * seeded rows alone (AC4) — they keep `example_kind = 'demo'`, which is what holds them
 * outside `billableCertificateFence()`.
 *
 * **A row can convert too, separately and later.** `ConvertDemoProductUseCase` and
 * `ConvertDemoCertificateUseCase` (VRG-836, `apps/api/.../demo/convert-demo-data/`)
 * clear a single row's `example_kind` — "this scraped guess is actually right, keep it".
 * Forward-only, one row at a time, and the two are separately gated because the stakes
 * differ: un-badging a PRODUCT is a claim about truth and is free, while un-badging a
 * CERTIFICATE is a decision to be billed for it now.
 *
 * So the badge marks a removable classification, and removing it is always an explicit
 * act by the brand.
 *
 * That is also the whole reason this badge is suppressed inside a demo company
 * (`useIsDemoCompany` below) and not after take-over. Before the company converts it is
 * Vera's from end to end, so every row carries `example_kind` and the badge discriminates
 * nothing. After, the rows that have not been converted sit beside rows the brand
 * entered, and the badge is the only thing saying which is which — including, crucially,
 * which ones a brand might want to convert.
 *
 * ## Why a 6px RECTANGLE and not the 999px pill
 *
 * Same distinction `AnnouncementKindBadge` and `SupportTypeBadge` document. A pill means
 * a LIFECYCLE state — something the record moves THROUGH, between values, as the record
 * progresses. This is a CLASSIFICATION of whose data the row is, decided when the row is
 * created.
 *
 * It is REMOVABLE — the VRG-836 convert-demo-data use-cases clear `example_kind`, see the
 * CORRECTION above, and the sentence that used to sit here ("a demo product does not
 * become a real one") is no longer true. But removable is not the same as lifecycle: the
 * value never moves between `demo` and `intro`, and conversion only ever ends the
 * classification, forward-only, by an explicit act. So the rectangle survives the
 * correction. It also sits in the same table row as an actual Status pill on the
 * Certificates list, where two pills would read as two states of one thing.
 *
 * The COLOUR treatment is shared (`statusTone`, all three parts) so this cannot drift
 * from the pills per light/dark mode. Only the silhouette differs. See the long note in
 * `AnnouncementKindBadge` for why hand-deriving a fill from the hue fails in dark mode.
 *
 * ## Tone: VERA brand purple, deliberately not a warning and not `info`
 *
 * Amber would say the row is a problem. It is not — it is doing its job, and the reader
 * is not being asked to act. So the register wanted is "worth knowing, findable later,
 * no alarm".
 *
 * That register was first built on `info`, whose cyan reads as a SEMANTIC status — the
 * same family as success/warning/error — which is wrong here: every other tone in that
 * family describes the state of the user's own data, and this badge describes whose data
 * it is. Brand purple (`brand[600]`, #7F48DF) says "Vera put this here", which is
 * literally what the badge means, and it cannot be mistaken for a status the brand is
 * expected to resolve.
 */

interface ExampleKindBadgeProps {
  /**
   * The row's `example_kind`. **Renders nothing when null/undefined**, which is the
   * common case (real customer data), so call sites can pass the field straight through
   * without guarding — `<ExampleKindBadge kind={product.example_kind} />`.
   */
  kind?: ExampleKind | null
  /**
   * `dense` for the metadata line inside a table cell — 20px tall, 11px label.
   *
   * Not the shared `STATUS_TOKEN_H` (28) there, and that is on purpose rather than an
   * oversight: 28px is the height of the STATUS BAND, where a badge sits beside a pill
   * and any difference is read instantly. Inside a cell the badge annotates a `body4`
   * metadata line — the SKU, the certificate id — and a 28px token on that line grows
   * every row in the table. Per-band sizing, per §29.
   */
  size?: 'default' | 'dense'
}

export const ExampleKindBadge = ({ kind, size = 'default' }: ExampleKindBadgeProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'
  /*
   * VRG-836 — inside a company whose own `kind` is `'demo'`, EVERY row is Vera's, so a
   * badge on every row is noise rather than information. Suppressed there and only
   * there: after `TakeOverDemoUseCase` moves the company to `'standard'` the seeded rows
   * keep `example_kind` and the badge goes back to marking the never-billed rows beside
   * the brand's own. See the CORRECTION in this file's docblock, and `useIsDemoCompany`.
   *
   * Decided HERE, not at the eight call sites, for the same reason the `!kind` guard
   * below is: three of those call sites are shared table cells with no company in scope,
   * and a guard spread over eight files is eight chances to forget it.
   */
  const inDemoCompany = useIsDemoCompany()

  // Real customer data. The overwhelmingly common case, handled here rather than at
  // ~8 call sites so no surface can forget the guard and render an empty badge.
  if (!kind || inDemoCompany) return null

  const label = EXAMPLE_KIND_LABEL[kind]
  if (!label) {
    // A kind written by a newer API than this bundle. Rendering an unlabelled badge is
    // worse than rendering nothing, and the console line is what makes it findable.
    // Mirrors `AnnouncementKindBadge` / `SupportTypeBadge`.
    console.error(`Invalid 'example_kind': ${kind}`)
    return null
  }

  const dense = size === 'dense'
  const { color, border, background } = statusTone(
    { color: theme.palette.brand[600] },
    isDark,
  )

  return (
    /*
     * `InfoTooltip`, not a raw MUI `Tooltip` — house body width, type scale and border
     * (§36), and it is what lets the badge reach the glossary at all.
     *
     * BOTH `term` and `text` are passed for `demo`, deliberately, and the pair is not
     * redundant:
     *
     * - `demo` is an `audience: internal` entry (§43/§44), so `useGlossaryTerm` returns
     *   undefined for a BRAND reader. `term` alone would therefore have made the tooltip
     *   render nothing at all on the surfaces this badge exists for — a brand looking at
     *   its own catalogue — which is a straight regression from today's copy.
     * - `text` keeps that copy as the visible body for everyone, and `InfoTooltip` still
     *   attaches the "Definition · Demo" footer when the entry DOES resolve. So staff get
     *   the full internal entry (why demos live on production, what is not billed) and a
     *   brand sees exactly what it sees today.
     *
     * `intro` NOW HAS ONE, and the asymmetry above is what made it worth writing: the two
     * kinds sit at different floors for a reason. `demo` is internal because a demo is
     * built from a prospect's own website and a brand should not be told that is what it
     * is looking at. `intro` is sample data Vera authored for a company to explore with —
     * there is nothing to hide, and the brand seeing the badge is precisely the reader who
     * needs to know those rows are not theirs and are never billed. So `intro` is a
     * `brand`-floor entry and resolves for everyone who can see the badge.
     *
     * `text` stays on both for the reason above: it is the only body a brand gets for
     * `demo`, and for `intro` it keeps the wording identical to what shipped while the
     * footer now reaches the fuller entry.
     */
    <InfoTooltip
      term={kind === 'demo' ? 'demo' : 'intro'}
      text={EXAMPLE_KIND_TOOLTIP[kind]}
      placement="top"
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: dense ? 1.5 : 2,
          minHeight: dense ? 20 : STATUS_TOKEN_H,
          // 6px, NOT the pill's 999px. Classification, not lifecycle state.
          borderRadius: 1.5,
          // Never let the badge be the thing that shrinks — the title beside it is
          // `noWrap` and truncates, which is the correct trade: a clipped title is
          // still recoverable from the row, a clipped badge is not.
          flexShrink: 0,
          width: 'fit-content',
          // The tooltip carries the explanation, so the badge itself is not clickable
          // and must not look it.
          cursor: 'default',
          color,
          border: `1px solid ${border}`,
          bgcolor: background,
        }}
      >
        <FlaskConical size={dense ? 11 : 14} strokeWidth={1.5} style={{ flexShrink: 0 }} />
        <Typography
          sx={{ fontSize: dense ? 11 : 12, fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      </Stack>
    </InfoTooltip>
  )
}
